import React, { useState } from 'react';
import { ArrowUpRight, ArrowDownRight, Info, HelpCircle } from 'lucide-react';
import { formatPct } from '../utils/kpiFormatters';

interface Props {
  title: string;
  value: string;
  icon: React.ReactNode;
  iconBg?: string;
  description?: string;
  previousValueFormatted?: string;
  deltaPct?: number;
  invertDeltaColor?: boolean;
  statusBadge?: {
    text: string;
    variant: 'optimal' | 'warning' | 'danger';
  };
  subtitle?: string;
}

export const KpiCard: React.FC<Props> = ({
  title,
  value,
  icon,
  iconBg = 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400',
  description,
  previousValueFormatted,
  deltaPct,
  invertDeltaColor = false,
  statusBadge,
  subtitle,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  // Determinar color de variación
  let isGood = false;
  let isBad = false;

  if (deltaPct !== undefined && deltaPct !== null && deltaPct !== 0) {
    if (invertDeltaColor) {
      isGood = deltaPct < 0;
      isBad = deltaPct > 0;
    } else {
      isGood = deltaPct > 0;
      isBad = deltaPct < 0;
    }
  }

  const deltaColorClass = isGood
    ? 'text-emerald-700 bg-emerald-50 dark:bg-emerald-950/50 dark:text-emerald-300'
    : isBad
    ? 'text-rose-700 bg-rose-50 dark:bg-rose-950/50 dark:text-rose-300'
    : 'text-text-muted bg-muted';

  return (
    <div className="relative rounded-2xl border border-border bg-surface p-4.5 shadow-xs transition hover:shadow-md hover:border-emerald-500/30 flex flex-col justify-between">
      {/* Encabezado de la Tarjeta */}
      <div>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-xs font-bold uppercase tracking-wider text-text-muted truncate">
              {title}
            </span>
            {description && (
              <div className="relative inline-block">
                <button
                  type="button"
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  onClick={() => setShowTooltip(!showTooltip)}
                  className="text-text-muted/60 hover:text-text-muted transition p-0.5 cursor-pointer"
                  title="¿Qué significa este indicador?"
                >
                  <HelpCircle size={13} />
                </button>
                {showTooltip && (
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 z-50 w-52 p-2.5 bg-neutral-900 text-white text-[11px] leading-relaxed rounded-xl shadow-lg pointer-events-none">
                    {description}
                    <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-neutral-900" />
                  </div>
                )}
              </div>
            )}
          </div>

          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${iconBg}`}>
            {icon}
          </span>
        </div>

        {/* Valor Principal */}
        <p className="text-2xl font-black tracking-tight text-text">
          {value}
        </p>
      </div>

      {/* Pie de la tarjeta con Delta y Meta */}
      <div className="mt-3 pt-2.5 border-t border-border/60 flex flex-wrap items-center justify-between gap-2 text-xs">
        {deltaPct !== undefined && deltaPct !== null ? (
          <div className="flex items-center gap-1.5">
            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md font-bold text-[11px] ${deltaColorClass}`}>
              {deltaPct > 0 ? <ArrowUpRight size={12} /> : deltaPct < 0 ? <ArrowDownRight size={12} /> : null}
              {formatPct(deltaPct)}
            </span>
            {previousValueFormatted && (
              <span className="text-[11px] text-text-muted">
                ant. {previousValueFormatted}
              </span>
            )}
          </div>
        ) : subtitle ? (
          <span className="text-[11px] text-text-muted">{subtitle}</span>
        ) : <div />}

        {statusBadge && (
          <span
            className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
              statusBadge.variant === 'optimal'
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                : statusBadge.variant === 'warning'
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                : 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300'
            }`}
          >
            {statusBadge.text}
          </span>
        )}
      </div>
    </div>
  );
};
